import React from 'react';
import { getPSPLandingViewPixel } from '@sparefoot/react-core/utils/tracking';

import { Page, Section, Container } from 'components/core/Page';
import { H3, Paragraph } from 'components/core/Typography';
import { SearchHero } from 'components/landing/SearchHero';

import './StorageNearMePage.scss';

import { PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import usePageViewTracking from 'hooks/usePageViewTracking';
import useCanonical from 'hooks/useCanonical';

export function StorageNearMePage() {
	const canonical = useCanonical('/storage-units-near-me');
	usePageViewTracking(PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING);
	return (
		<Page
			className="storage-near-landing-page"
			trackingPixel={getPSPLandingViewPixel('storage')}
			pageType={PAGE_SUB_TYPES.STORAGE_NEAR_ME_LANDING}
			title="Find Cheap Self-Storage Units Near You - SelfStorage.com"
			description="With over 40,000 storage facilities, SelfStorage.com helps you rent cheap storage units near you. Check local prices, amenities, & reserve online."
			canonical={canonical}
		>
			<SearchHero
				headline="Self-Storage Units Near You"
				subheading="Find self-storage units near you"
				className="storage-near-search-hero"
				withSearchType={false}
			/>
			<Section>
				<Container>
					<H3 size="large">
						Find Self-Storage Units Near You with SelfStorage.com
					</H3>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">
						10,000+ self-storage locations at your fingertips
					</H3>
					<Paragraph>
						SelfStorage.com works with more than 10,000 self-storage
						facilities to list the largest inventory of self-storage
						units nationwide—the most of any website today. Our
						extensive coverage ranges from major cities to rural
						areas. You’ll always have a place to store your things,
						whether it’s in New York City or Skokie, IL.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">Secure self-storage you can trust</H3>
					<Paragraph>
						Moving to a new neighborhood? SelfStorage.com shows you
						reviews from storage customers like you, so you know
						exactly what to expect when you arrive. Each facility on
						our website is vetted by our team to meet high-quality
						standards. When you use SelfStorage.com, you’re
						guaranteed the very best, every time.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">
						Get budget self-storage with exclusive rates and
						discounts
					</H3>
					<Paragraph>
						We're committed to bringing you affordable, high-quality
						self-storage. Get exclusive discounts when you use
						SelfStorage.com to find and reserve your storage unit.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">
						A better way to search for self-storage
					</H3>
					<Paragraph>
						Type in the zip code or city where you need self-storage
						to view available self-storage units near you. Quickly
						sort and compare your results by price, amenities,
						distance, ratings and more. Need to hit a certain price
						point in your self-storage search? SelfStorage.com gives
						you the ability to expand your search radius to find the
						perfect storage unit prices for your budget.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">Reserve a self-storage unit instantly</H3>
					<Paragraph>
						When you’re ready to reserve your self-storage unit,
						enter your contact information and planned start date.
						We’ll notify the storage facility of your reservation
						and send you instant confirmation via email.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">100% free to reserve. Cancel anytime</H3>
					<Paragraph>
						Reserving a self-storage unit with SelfStorage.com is
						100% free with no obligations and free cancellations
						anytime.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">Why use self-storage?</H3>
					<Paragraph>
						Self-storage is the perfect solution for anyone who
						needs extra space at a low cost. Every year, millions of
						movers, renovators, downsizers, college students and
						businesses use self-storage to give themselves some more
						space. Self-storage can also be ideal for wine storage,
						band practice, vehicles, small commercial businesses and
						long-term storage.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">How self-storage works</H3>
					<Paragraph>
						In the U.S., businesses called self-storage facilities
						allow you to rent out space (called “self-storage
						units”) on their property. You pay for the space as you
						would an apartment rental. Self-storage facilities range
						from the independently operated mom-and-pop, to regional
						chains, to national corporations.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">Self-storage leases</H3>
					<Paragraph>
						Renting a self-storage unit is typically month-to-month,
						although this may vary for some individual locations.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">Self-storage amenities</H3>
					<Paragraph>
						Storage facilities also offer a variety of amenities,
						such as climate control, 24-hour access, electrical
						outlets and more. If you’re in need of a specific
						amenity, check with your store manager before reserving
						a unit.
					</Paragraph>
				</Container>
			</Section>
			<Section>
				<Container>
					<H3 size="medium">Self-storage fees</H3>
					<Paragraph>
						In addition to monthly rent, you may be required to pay
						insurance (waived if you have homeowners insurance) and
						a one-time administrative fee at move-in. These are
						common storage industry practices that pay for the cost
						of signing up a new tenant.
					</Paragraph>
				</Container>
			</Section>
		</Page>
	);
}

export default StorageNearMePage;
